<template>
    <div id="cic-c" class="compound-interest-calculator-container">
        <PageHeader
            title="Calculadora de Interés Compuesto"
            subtitle="Herramienta para calcular el importe total obtenido al pasar el tiempo establecido de tu inversión."
        />

        <div v-if="show_popup" class="popup-overlay">
            <div class="popup-modal card">
                <button class="material-icons-outlined btn-close-popup" @click="_closePopup()">close</button>
                <div class="logo-eurekers">
                    <img src="../assets/img/eurekers.jpg" title="logo-eurekers" width="50" height="50" alt="Eurekers Logo" />
                </div>
                <div class="popup-form-container">
                    <p class="typo-body-1">
                        En Eurekers tenemos herramientas como esta, que te ayudarán a gestionar tus inversiones. Si quieres, déjanos tu teléfono e email para que te podamos enviar más información.
                    </p>
                    <div class="form-group">
                        <label for="popup-tlf">Teléfono</label>
                        <input type="text" id="popup-tlf" class="form-input" v-model="user_tlf_str" placeholder="Ej: 600123456">
                    </div>
                    <div class="form-group">
                        <label for="popup-email">Email</label>
                        <input type="email" id="popup-email" class="form-input" v-model="user_email_str" placeholder="Ej: tu@email.com">
                    </div>
                    <button class="button button-primary btn-send-popup" @click="_sendInfo()">ENVIAR</button>
                </div>
            </div>
        </div>

        <div class="main-content-grid">
            <div class="form-panel card">
                <h3 class="typo-headline-6 section-title">Datos para la Simulación</h3>

                <div class="form-group">
                    <label for="cic-initial-capital">Capital Inicial (€)</label>
                    <input type="text" id="cic-initial-capital" class="form-input" v-model="initial_capital_str" @input="handleNumericInput('initial_capital_str')" placeholder="Ej: 10000">
                </div>

                <div class="input-group-row">
                    <div class="form-group">
                        <label for="cic-contribution">Aportación Periódica (€)</label>
                        <input type="text" id="cic-contribution" class="form-input" v-model="contribution_value_str" @input="handleNumericInput('contribution_value_str')" placeholder="Ej: 500">
                    </div>
                    <div class="form-group">
                        <label for="cic-contribution-frequency">Frecuencia de Aportación</label>
                        <select id="cic-contribution-frequency" class="form-select" v-model="contribution_frequency_val">
                            <option value="monthly-contribution">Mensual</option>
                            <option value="trimesterly-contribution">Trimestral</option>
                            <option value="quarterly-contribution">Cuatrimestral</option>
                            <option value="semesterly-contribution">Semestral</option>
                            <option value="annual-contribution">Anual</option>
                        </select>
                    </div>
                </div>

                <div class="input-group-row">
                    <div class="form-group">
                        <label for="cic-annual-interest-rate">Tasa de Interés Anual (%)</label>
                        <input type="text" id="cic-annual-interest-rate" class="form-input" v-model="annual_interest_rate_str" @input="handleNumericInput('annual_interest_rate_str')" placeholder="Ej: 5">
                    </div>
                     <div class="form-group">
                        <label for="cic-interest-payment-frequency">Capitalización de Intereses</label>
                        <select id="cic-interest-payment-frequency" class="form-select" v-model="interest_payment_frequency_val">
                            <option value="monthly">Mensual</option>
                            <option value="trimesterly">Trimestral</option>
                            <option value="quarterly">Cuatrimestral</option>
                            <option value="semesterly">Semestral</option>
                            <option value="annual">Anual</option>
                        </select>
                    </div>
                </div>

                <div class="input-group-row">
                    <div class="form-group">
                        <label for="cic-period">Período de Inversión</label>
                        <input type="text" id="cic-period" class="form-input" v-model="period_value_str" @input="handleNumericInput('period_value_str')" placeholder="Ej: 10">
                    </div>
                    <div class="form-group">
                        <label for="cic-period-unit">Unidad del Período</label>
                        <select id="cic-period-unit" class="form-select" v-model="period_unit_val">
                            <option value="years">Años</option>
                            <option value="months">Meses</option>
                            <option value="trimesters">Trimestres</option>
                            <option value="quarters">Cuatrimestres</option>
                            <option value="semesters">Semestres</option>
                        </select>
                    </div>
                </div>
                <button class="button button-primary button-calculate" @click="calculateCompoundInterest">CALCULAR</button>
            </div>

            <div v-if="results_visible" class="results-panel card">
                <h3 class="typo-headline-6 section-title">Resultados del Cálculo</h3>
                <div class="result-box">
                    <p class="result-label">Balance Final</p>
                    <p class="result-value result-highlight">{{ final_capital_processed }}</p>
                </div>
                <div class="result-box">
                    <p class="result-label">Aportaciones Totales</p>
                    <p class="result-value">{{ total_deposits_processed }}</p>
                </div>
                <div class="result-box">
                    <p class="result-label">Intereses Totales Ganados</p>
                    <p class="result-value">{{ total_interests_processed }}</p>
                </div>
            </div>
        </div>

        <div v-if="results_visible" class="chart-panel card">
            <h3 class="typo-headline-6 section-title">Proyección Gráfica del Interés Compuesto</h3>
            <div id="cic-chart-container"></div>
        </div>
    </div>
</template>

<script>
/* HighChart */
import Highcharts from "highcharts";
import Vue from 'vue'; // Keep Vue import if nextTick is used
import { mapGetters } from "vuex";
import APICaller from "@/components/codeComponents/APICaller.js";
import PageHeader from "@/components/common/PageHeader.vue";
// Removed InputTextOutline, SelectOutline, and their JSON config imports

export default {
    mixins: [APICaller],
    name: 'CompoundInterestCalculator',
    components: {
        PageHeader,
        // Removed InputTextOutline, SelectOutline
    },
    metaInfo() {
        return {
            title: 'Calculadora de Interés Compuesto Eurekers',
            meta: [
                { name: 'description', content: "La calculadora de interés compuesto permite calcular el importe total obtenido al pasar el tiempo establecido en tu inversión, ofrecida por Eurekers" }
            ]
        }
    },
    data() {
        return {
            // String-based inputs for v-model
            initial_capital_str: '',
            contribution_value_str: '',
            annual_interest_rate_str: '',
            period_value_str: '',

            // Select values
            contribution_frequency_val: 'monthly-contribution', // Default value
            period_unit_val: 'years', // Default value
            interest_payment_frequency_val: 'annual', // Default value
            
            user_tlf_str: '',
            user_email_str: '',

            // Original data properties (some might be redundant or need renaming)
            values_accrued_interest: [], // For chart calculation
            popup_counter: 0,
            show_popup: false,

            final_capital: 0,
            total_deposits: 0,
            total_interests: 0,
            results_visible: false, // Renamed from res_container

            // HighChart data series
            initial_investment_data: [], // Renamed
            additional_deposits_data: [],
            accumulated_interest_data: [],
            x_categories: [],
        };
    },
    computed: {
        ...mapGetters(['is_mobile_device']),

        // Parsed numeric values from string inputs
        parsed_initial_capital() { return this._getParsedNumericValue(this.initial_capital_str); },
        parsed_contribution_value() { return this._getParsedNumericValue(this.contribution_value_str); },
        parsed_annual_interest_rate() { return this._getParsedNumericValue(this.annual_interest_rate_str); },
        parsed_period_value() { return this._getParsedIntValue(this.period_value_str); },

        // Processed results for display
        final_capital_processed() { return this.formatCurrency(this.final_capital); },
        total_deposits_processed() { return this.formatCurrency(this.total_deposits); },
        total_interests_processed() { return this.formatCurrency(this.total_interests); },
    },
    created() {
        this._isIframe();
    },
    methods: {
        handleNumericInput(fieldName) {
            let value = this[fieldName];
            if (typeof value !== 'string') value = String(value);
            // Allow digits, one comma or one dot for decimals
            value = value.replace(/[^0-9.,]/g, '');
            const parts = value.split(/[.,]/);
            if (parts.length > 2) { // If more than one separator
                value = parts[0] + '.' + parts.slice(1).join('');
            } else if (parts.length === 2) {
                value = parts[0] + '.' + parts[1];
            }
            this[fieldName] = value;
        },
        _getParsedNumericValue(value) {
            if (value === null || value === undefined || String(value).trim() === '') return 0;
            const sValue = String(value).replace(',', '.');
            const num = parseFloat(sValue);
            return isNaN(num) ? 0 : num;
        },
        _getParsedIntValue(value) {
            if (value === null || value === undefined || String(value).trim() === '') return 0;
            // Remove all commas and dots for integers
            const sValue = String(value).replace(/[,.]/g, '');
            const num = parseInt(sValue, 10);
            return isNaN(num) ? 0 : num;
        },
        formatCurrency(value) {
            const num = Number(value);
            if (isNaN(num)) return "0,00 €";
            return num.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
        },

        _sendInfo() {
            const emailValid = this._emailValidator(this.user_email_str);
            const tlfValid = this._tlfValidator(this.user_tlf_str);

            if (emailValid && tlfValid) {
                var cookieStorage = window.localStorage;
                cookieStorage.setItem("email", this.user_email_str);
                cookieStorage.setItem("phone_number", this.user_tlf_str);

                let data = new URLSearchParams();
                data.append('email', this.user_email_str);
                data.append('tlf', this.user_tlf_str);
                this._postLeadInfo(data); // Renamed for clarity
            }
        },

        _postLeadInfo(data) { // Renamed from _postInfo
            let success = response => {
                if (response.status == 200) {
                    this._closePopup();
                    this.$vueOnToast.pop('success', 'Información enviada', 'Gracias por tu interés. Nos pondremos en contacto contigo.');
                } else {
                    this.$vueOnToast.pop('error', 'Error al enviar', 'Ha ocurrido un error. Por favor, inténtelo de nuevo.');
                }
            };
            let url = "/calculadora/datos";
            const successHandler = new APICaller.SuccessHandler(success);
            const failureHandler = new APICaller.FailureHandler('CIC-LeadPost', 'Error en el envío de datos del lead.');
            this._postAPICall(url, data, successHandler, failureHandler);
        },

        _tlfValidator: function (valor) {
            if (/^\d{9}$/.test(valor)) {
                return true;
            } else {
                this.$vueOnToast.pop('error', 'Teléfono incorrecto', 'El número de teléfono debe tener 9 dígitos.');
                return false;
            }
        },

        _emailValidator: function (valor) {
            if (/^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/.test(valor)) {
                return true;
            } else {
                this.$vueOnToast.pop('error', 'Email incorrecto', 'Por favor, introduce un email válido.');
                return false;
            }
        },

        _openPopUp: function () {
            var cookieStorage = window.localStorage;
            var em = cookieStorage.getItem("email");
            var tel = cookieStorage.getItem("phone_number");
            if ((this.popup_counter === 3 || this.popup_counter === 10) && (!em || !tel)) {
                this.show_popup = true;
            }
        },

        _closePopup() {
            this.show_popup = false;
        },

        calculateCompoundInterest() { // Renamed from compound_interest
            // Reset chart data
            this.initial_investment_data = [];
            this.additional_deposits_data = [];
            this.values_accrued_interest = []; // Intermediate calculation step
            this.accumulated_interest_data = [];
            this.x_categories = [];

            const months_in_year = 12;

            // Get parsed data
            const Ci = this.parsed_initial_capital; // Initial Capital
            const r_annual_percentage = this.parsed_annual_interest_rate; // Annual interest rate as percentage
            const r_decimal = r_annual_percentage / 100; // Annual interest rate as decimal
            let t_input_period = this.parsed_period_value; // Period length (e.g., 10)
            const t_period_unit = this.period_unit_val; // Unit of period (e.g., 'years', 'months')
            const Cr = this.parsed_contribution_value; // Contribution amount per period
            const contribution_freq = this.contribution_frequency_val; // e.g., 'monthly-contribution'
            const n_compounding_freq_key = this.interest_payment_frequency_val; // e.g., 'annual', 'monthly'

            // Validate inputs
            if (Ci === 0 && Cr === 0) {
                 this.$vueOnToast.pop('warning', 'Datos incompletos', 'Introduce un capital inicial o una aportación periódica.');
                 this.results_visible = false;
                 return;
            }
            if (r_annual_percentage <= 0 || t_input_period <= 0) {
                this.$vueOnToast.pop('warning', 'Datos inválidos', 'La tasa de interés y el período deben ser positivos.');
                this.results_visible = false;
                return;
            }
             if (Ci < 0 || Cr < 0 || r_annual_percentage < 0 || t_input_period < 0 ) {
                this.$vueOnToast.pop('error', 'Valores negativos', 'Por favor, introduzca valores positivos.');
                this.results_visible = false;
                return;
            }


            // Convert t_input_period to years for calculation consistency (t_years)
            let t_years;
            switch (t_period_unit) {
                case 'months': t_years = t_input_period / months_in_year; break;
                case 'trimesters': t_years = (t_input_period * 3) / months_in_year; break;
                case 'quarters': t_years = (t_input_period * 4) / months_in_year; break; // Assuming cuatrimestres means 4 months
                case 'semesters': t_years = (t_input_period * 6) / months_in_year; break;
                default: t_years = t_input_period; // 'years'
            }
            if (t_years <= 0) {
                 this.$vueOnToast.pop('error', 'Período inválido', 'El período de inversión resulta en cero o menos años.');
                 this.results_visible = false;
                 return;
            }


            // s: Number of contributions per year
            let s_contributions_per_year;
            switch (contribution_freq) {
                case 'monthly-contribution': s_contributions_per_year = 12; break;
                case 'trimesterly-contribution': s_contributions_per_year = 4; break;
                case 'quarterly-contribution': s_contributions_per_year = 3; break; // 3 cuatrimestres en un año
                case 'semesterly-contribution': s_contributions_per_year = 2; break;
                default: s_contributions_per_year = 1; // 'annual-contribution'
            }

            // n: Number of times interest is compounded per year
            let n_compounding_per_year;
            switch (n_compounding_freq_key) {
                case 'monthly': n_compounding_per_year = 12; break;
                case 'trimesterly': n_compounding_per_year = 4; break;
                case 'quarterly': n_compounding_per_year = 3; break;
                case 'semesterly': n_compounding_per_year = 2; break;
                default: n_compounding_per_year = 1; // 'annual'
            }

            // --- Main Calculation Logic ---
            // Formula: Cf = Ci * (1 + r/n)^(n*t) + Cr * [ ((1 + r/n)^(n*t) - 1) / ((1 + r/n)^(n/s) - 1) ]
            // This formula assumes Cr is contributed s times per year, and interest r is compounded n times per year.

            let Cf_final_capital;

            if (r_decimal === 0) { // No interest
                 Cf_final_capital = Ci + (Cr * s_contributions_per_year * t_years);
            } else {
                const nt = n_compounding_per_year * t_years;
                const r_over_n = r_decimal / n_compounding_per_year;
                
                const factor_common = Math.pow(1 + r_over_n, nt);
                const Cf_first_component = Ci * factor_common;

                let Cf_second_component = 0;
                if (Cr > 0 && s_contributions_per_year > 0) {
                    if (n_compounding_per_year === s_contributions_per_year) { // Interest compounding and contribution frequency match
                        // Simplified formula: Cr * [ ((1 + r/n)^(n*t) - 1) / (r/n) ]
                        Cf_second_component = Cr * ((factor_common - 1) / r_over_n);
                    } else {
                         // General formula for when n != s
                        const ns_factor = n_compounding_per_year / s_contributions_per_year;
                        // Ensure the base for the power in denominator is > 0 and not 1, otherwise division by zero or complex numbers.
                        const denominator_base = Math.pow(1 + r_over_n, ns_factor);
                        if (denominator_base !== 1) {
                             Cf_second_component = Cr * ((factor_common - 1) / (denominator_base - 1));
                        } else {
                            // This case occurs if r_over_n is 0 (already handled) or ns_factor is 0 (not possible as n,s > 0).
                            // Or if (1+r/n)^ns_factor = 1. This can happen if ns_factor is such that it results in 1.
                            // Fallback to simpler sum if this complex case leads to issues.
                            // For simplicity, if denominator is 1 (meaning (1+r/n)^(n/s) = 1), approximate contributions without this specific compounding effect.
                            // This is a complex edge case. The original formula might be simplified or have conditions.
                            // A common simplification is to sum contributions and then apply compound interest, or assume n=s.
                            // Let's assume a simple sum of contributions for this edge case.
                            Cf_second_component = Cr * s_contributions_per_year * t_years; // Approximation for this edge case.
                            // A more robust solution would involve iterative calculation if this specific formula edge case is critical.
                        }
                    }
                }
                 Cf_final_capital = Cf_first_component + Cf_second_component;
            }
            
            this.final_capital = Cf_final_capital;
            const total_contributions_made = Cr * s_contributions_per_year * t_years;
            this.total_deposits = Ci + total_contributions_made;
            this.total_interests = Cf_final_capital - this.total_deposits;

            this.results_visible = true;
            this._buildChartSeries(Ci, Cr, contribution_freq, r_decimal, n_compounding_freq_key, t_years, t_period_unit, t_input_period);
            
            this.popup_counter++;
            this._openPopUp();

            if (this.is_mobile_device) {
                this.$nextTick(() => {
                    const resultsElement = this.$el.querySelector('.results-panel') || this.$el.querySelector('.chart-panel');
                    if (resultsElement) {
                        resultsElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                });
            }
        },

        _buildChartSeries(Ci, Cr, contribution_freq, r_decimal, n_compounding_freq_key, t_total_years, t_period_unit_for_label, t_input_period_for_label) {
            this.initial_investment_data = [];
            this.additional_deposits_data = [];
            this.accumulated_interest_data = [];
            this.x_categories = [];

            const display_periods = Math.ceil(t_total_years); // Chart will show data per year, up to total years
            let current_balance = Ci;
            let total_contributions_chart = 0;
            let total_interest_chart = 0;

            // Determine contributions per year for chart series
            let annual_contribution_amount;
            switch (contribution_freq) {
                case 'monthly-contribution': annual_contribution_amount = Cr * 12; break;
                case 'trimesterly-contribution': annual_contribution_amount = Cr * 4; break;
                case 'quarterly-contribution': annual_contribution_amount = Cr * 3; break;
                case 'semesterly-contribution': annual_contribution_amount = Cr * 2; break;
                default: annual_contribution_amount = Cr; // annual
            }
            
            // Determine compounding periods per year
            let n_comp_per_year;
             switch (n_compounding_freq_key) {
                case 'monthly': n_comp_per_year = 12; break;
                case 'trimesterly': n_comp_per_year = 4; break;
                case 'quarterly': n_comp_per_year = 3; break;
                case 'semesterly': n_comp_per_year = 2; break;
                default: n_comp_per_year = 1; // 'annual'
            }

            for (let year = 0; year <= display_periods; year++) {
                if (year === 0) {
                    this.initial_investment_data.push(Ci);
                    this.additional_deposits_data.push(0);
                    this.accumulated_interest_data.push(0);
                    this.x_categories.push("Inicio");
                    continue;
                }
                
                // Calculate balance at the end of 'year'
                // This is an iterative calculation for chart clarity, year by year.
                // Contributions for the current year
                let contributions_this_year = 0;
                if (year <= t_total_years) { // Only add contributions within the investment period
                     contributions_this_year = annual_contribution_amount;
                } else if (year > t_total_years && (t_total_years % 1 !== 0)) { // Partial final year contributions
                     contributions_this_year = annual_contribution_amount * (t_total_years % 1);
                }


                let interest_this_year_total = 0;
                let balance_at_start_of_year = current_balance;

                // Simplified: apply contributions at start of year, then compound
                // More accurate: distribute contributions and compound according to n_comp_per_year and contribution_freq
                // For simplicity for the chart, we'll compound annually on the (balance + annual_contributions)
                // This is a simplification. The main formula calculates the end result accurately.
                // The chart provides a year-by-year breakdown.
                
                let balance_after_contrib = balance_at_start_of_year + (year <= t_total_years ? contributions_this_year : 0) ;
                let interest_earned_this_year_approx = 0;

                if (r_decimal > 0) {
                     // Approximate interest for the year based on annual compounding for chart simplicity
                     interest_earned_this_year_approx = balance_after_contrib * Math.pow(1 + r_decimal / n_comp_per_year, n_comp_per_year) - balance_after_contrib;
                }
                
                current_balance = balance_after_contrib + interest_earned_this_year_approx;
                total_contributions_chart += (year <= t_total_years ? contributions_this_year : 0);
                total_interest_chart += interest_earned_this_year_approx;


                this.initial_investment_data.push(Ci); // Stays constant
                this.additional_deposits_data.push(Math.round(total_contributions_chart));
                this.accumulated_interest_data.push(Math.round(total_interest_chart));
                
                // X-axis labels
                let label_period_unit_singular = "Año";
                switch (t_period_unit_for_label) {
                    case 'months': label_period_unit_singular = year * 12 <= t_input_period_for_label ? "Mes" : "Año"; break;
                    case 'trimesters': label_period_unit_singular = year * 4 <= t_input_period_for_label ? "Trim." : "Año"; break;
                    case 'quarters': label_period_unit_singular = year * 3 <= t_input_period_for_label ? "Cuat." : "Año"; break;
                    case 'semesters': label_period_unit_singular = year * 2 <= t_input_period_for_label ? "Sem." : "Año"; break;
                }
                 this.x_categories.push(`${label_period_unit_singular} ${year}`);

                if (year >= t_total_years && t_total_years % 1 !== 0 && year < display_periods) { //If investment period ends mid-year
                    // No further changes after full period for chart
                } else if (year >= t_total_years) {
                     //break; // Stop chart after investment period if it's a whole number of years
                }
            }
            this._renderHighchart();
        },

        _isIframe() {
            try {
                if (window.self !== window.top && window.location.search.includes("iframe")) {
                    const sidebar = document.getElementById("sidebar-nav-container");
                    if (sidebar) sidebar.style.display = "none";
                }
            } catch (e) {
                // Catch potential cross-origin errors if in an iframe and trying to access top
                console.warn("Could not access window.top, possibly due to cross-origin restrictions.");
                 if (window.location.search.includes("iframe")) { // Fallback for restricted environments
                    const sidebar = document.getElementById("sidebar-nav-container");
                    if (sidebar) sidebar.style.display = "none";
                }
            }
        },

        _renderHighchart: function () {
            const self = this;
            this.$nextTick(function () { // Use this.$nextTick for DOM updates
                Highcharts.chart('cic-chart-container', {
                    chart: {
                        type: 'column',
                        backgroundColor: 'var(--color-surface)',
                        style: { fontFamily: "'Open Sans', sans-serif" }
                    },
                    title: { text: '', style: { color: 'var(--color-text-primary)' } },
                    xAxis: {
                        categories: self.x_categories,
                        title: { text: 'Período', style: { color: 'var(--color-text-secondary)' } },
                        labels: { style: { color: 'var(--color-text-secondary)' } },
                        lineColor: 'var(--color-outline)',
                        tickColor: 'var(--color-outline)'
                    },
                    yAxis: {
                        min: 0,
                        title: { text: 'Capital (€)', style: { color: 'var(--color-text-secondary)' } },
                        labels: { style: { color: 'var(--color-text-secondary)' } },
                        gridLineColor: 'var(--color-outline-variant)',
                        stackLabels: { // Show total on top of stacked columns
                            enabled: true,
                            style: {
                                fontWeight: 'bold',
                                color: (Highcharts.defaultOptions.title.style && Highcharts.defaultOptions.title.style.color) || 'gray'
                            },
                            formatter: function() {
                                return self.formatCurrency(this.total);
                            }
                        }
                    },
                    tooltip: {
                        formatter: function () {
                            let s = `<b>${this.x}</b>`;
                            this.points.forEach(function (point) {
                                s += `<br/><span style="color:${point.series.color}">${point.series.name}</span>: ${self.formatCurrency(point.y)}`;
                            });
                            s += `<br/><b>Total: ${self.formatCurrency(this.points.reduce((sum, point) => sum + point.y, 0))}</b>`;
                            return s;
                        },
                        shared: true,
                        backgroundColor: 'var(--color-background-tooltip, rgba(0,0,0,0.8))',
                        borderColor: 'var(--color-outline)',
                        style: { color: 'var(--color-text-on-tooltip, #fff)' }
                    },
                    plotOptions: {
                        column: {
                            stacking: 'normal',
                            dataLabels: { // Optional: show individual values on columns
                                enabled: false, // Keep false to avoid clutter, total is shown with stackLabels
                            }
                        }
                    },
                    series: [{
                        name: 'Inversión Inicial',
                        data: self.initial_investment_data,
                        color: 'var(--color-chart-initial, #003366)' // Example: Dark blue
                    }, {
                        name: 'Aportaciones Periódicas Acumuladas',
                        data: self.additional_deposits_data,
                        color: 'var(--color-chart-contributions, #00ADD9)' // Example: Primary Eurekers blue
                    }, {
                        name: 'Intereses Acumulados',
                        data: self.accumulated_interest_data,
                        color: 'var(--color-chart-interest, #85C1E9)' // Example: Lighter blue
                    }],
                    credits: { enabled: false },
                    responsive: {
                        rules: [{
                            condition: { maxWidth: 600 },
                            chartOptions: {
                                legend: { layout: 'horizontal', align: 'center', verticalAlign: 'bottom' },
                                yAxis: { stackLabels: { enabled: false } } // Disable stack labels on small screens if too cluttered
                            }
                        }]
                    }
                });
            })
        },
    },
}
</script>

<style lang="scss" scoped>
// Import global styles or define variables if not already available
// For example: @import '@/styles/variables.scss';

.compound-interest-calculator-container {
    background-color: var(--color-background);
    color: var(--color-text-primary);
    padding: 20px 5%;
    min-height: 100vh;
    max-width: 1200px;
    margin: 0 auto;
    box-sizing: border-box;
}

.card {
    background-color: var(--color-surface);
    border-radius: 12px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.07);
    padding: 25px 30px;
    margin-bottom: 30px;
    border: 1px solid var(--color-outline);
}

.section-title {
    color: var(--color-text-primary);
    margin-top: 0;
    margin-bottom: 25px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--color-outline);
    display: block; // Ensures border spans full width if title is short
}

.main-content-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
    gap: 30px;
    align-items: start; // Important for panels of different heights
}

.form-panel {
    // Specific styles for form panel if needed
}

.form-group {
    display: flex;
    flex-direction: column;
    margin-bottom: 20px;

    label {
        display: block;
        margin-bottom: 8px;
        font-size: 0.875rem; // 14px
        font-weight: 500;
        color: var(--color-text-secondary);
    }
}

.input-group-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 20px;
    .form-group {
        margin-bottom: 0; // Remove bottom margin from individual groups in a row
    }
}

.form-input,
.form-select {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid var(--color-outline);
    border-radius: 6px;
    background-color: var(--color-input-background, var(--color-surface));
    color: var(--color-text-primary);
    font-size: 1rem; // 16px
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: 0 0 0 3px var(--color-primary-alpha, rgba(15, 174, 215, 0.25));
    }

    &::placeholder {
        color: var(--color-text-placeholder, var(--color-text-inactive));
        opacity: 0.7;
    }
}

.form-select {
    appearance: none; // Basic reset
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23333'%3E%3Cpath d='M8 11.207l-4.66-4.66a.5.5 0 0 1 .707-.707L8 9.793l4.053-4.053a.5.5 0 1 1 .707.707L8 11.207z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 15px center;
    background-size: 16px 16px;
    padding-right: 40px; // Space for arrow
}


.button {
    padding: 12px 25px;
    font-size: 1rem;
    font-weight: 600; // Bolder buttons
    border-radius: 6px;
    cursor: pointer;
    transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease, transform 0.1s ease;
    border: 1px solid transparent;
    text-align: center;
    display: block; // Makes it take full width by default
    width: 100%; // Ensures it takes full width of its container
    margin-top: 10px; // Add some space above button

    &.button-primary {
        background-color: var(--color-highlight); // Use highlight for main CTA
        color: var(--color-on-primary, #fff);
        border-color: var(--color-highlight);

        &:hover {
            background-color: var(--color-primary-dark, hsl(195, 95%, 40%)); // Slightly darker shade
            border-color: var(--color-primary-dark, hsl(195, 95%, 40%));
        }
        &:active {
            transform: translateY(1px);
        }
    }
}
.button-calculate {
    margin-top: 20px; // More space before main calculate button
}

.results-panel {
    // Specific styles for results panel
}

.result-box {
    background-color: var(--color-active-surface, var(--color-surface-variant));
    padding: 15px 20px;
    border-radius: 8px;
    border: 1px solid var(--color-outline-variant, var(--color-outline));
    margin-bottom: 15px;

    &:last-child {
        margin-bottom: 0;
    }

    .result-label {
        font-size: 0.8125rem; // 13px
        font-weight: 600;
        color: var(--color-text-secondary);
        margin-bottom: 5px;
        text-transform: uppercase;
        letter-spacing: 0.03em;
    }

    .result-value {
        font-size: 1.6rem; // Larger result values
        font-weight: 700;
        color: var(--color-text-primary);
        margin: 0 0 5px 0;
        line-height: 1.2;

        &.result-highlight {
            color: var(--color-primary); // Emphasize key result
        }
    }
}

.chart-panel {
    // Specific styles for chart panel
}

#cic-chart-container {
    height: 450px;
    width: 100%;
}

// Typography (can be in a global file)
.typo-headline-6 { font-size: 1.25rem; font-weight: 500; line-height: 1.4; color: var(--color-text-primary); }
.typo-body-1 { font-size: 1rem; line-height: 1.6; color: var(--color-text-secondary); }


// Popup Styles
.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 20px;
    box-sizing: border-box;
}

.popup-modal {
    position: relative; // For close button positioning
    width: 100%;
    max-width: 500px;
    padding: 30px 35px; // More padding
    animation: fadeInScale 0.3s ease-out;

    .logo-eurekers {
        text-align: center;
        margin-bottom: 20px;
        img {
            border-radius: 50%; // Rounded logo
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
    }

    .popup-form-container {
        p {
            margin-bottom: 20px;
            text-align: center;
        }
        .form-group { margin-bottom: 15px; } // Tighter spacing in popup
    }

    .btn-close-popup {
        position: absolute;
        top: 15px;
        right: 15px;
        background: none;
        border: none;
        font-size: 1.8rem; // Larger close icon
        color: var(--color-text-secondary);
        cursor: pointer;
        padding: 5px;
        line-height: 1;
        &:hover {
            color: var(--color-text-primary);
        }
    }
    .btn-send-popup {
        margin-top: 20px;
    }
}

@keyframes fadeInScale {
    from {
        opacity: 0;
        transform: scale(0.95);
    }
    to {
        opacity: 1;
        transform: scale(1);
    }
}


// Responsive adjustments
@media (max-width: 768px) {
    .main-content-grid {
        grid-template-columns: 1fr; // Stack form and results on smaller screens
    }
    .input-group-row {
        grid-template-columns: 1fr; // Stack inputs in a row
        gap: 0; // Remove gap, rely on form-group margin
        .form-group {
             margin-bottom: 20px; // Add back margin when stacked
             &:last-child { margin-bottom: 0; }
        }
    }
    .card {
        padding: 20px 25px;
    }
    .popup-modal {
        padding: 25px 30px;
    }
}

@media (max-width: 600px) {
    .compound-interest-calculator-container {
        padding: 15px;
    }
    .card {
        padding: 20px;
    }
     .result-value {
        font-size: 1.4rem;
    }
    #cic-chart-container {
        height: 350px; // Shorter chart on small screens
    }
    .popup-modal {
        .btn-close-popup { top: 10px; right: 10px; font-size: 1.6rem; }
    }
}
</style>